import { Autocomplete, Avatar, Box, Button, Chip, CircularProgress, FormControl, MenuItem, Modal, Select, TextField, Typography, Popover, List, ListItem, Tabs, Tab, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DateNavigator, DayView, Toolbar } from "@devexpress/dx-react-scheduler-material-ui";
import { ViewState, EditingState, IntegratedEditing } from "@devexpress/dx-react-scheduler";
import { Scheduler, WeekView, MonthView, Appointments, AppointmentForm } from "@devexpress/dx-react-scheduler-material-ui";
import ContentBox from "../../components/ui/ContentBox";
import { modalStyle, modalStyleOverflow, modalStyleVertical } from "../../theme/ModalStyle";
import ButtonSquare from "../../components/ui/ButtonSquare";
import moment from "moment";
import PersonIcon from "@mui/icons-material/Person";
import { ToastContainer, toast } from "react-toastify";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { useAuthContext } from "../../hooks/useAuthContext";
import * as yup from "yup";
import { ErrorMessage, Form, Formik } from "formik";
import { hours_time } from "../../components/data/hours";
import FormikErrorText from "../../components/ui/FormikErrorText";
import MySwitch from "../../components/ui/MySwitch/MySwitch";
import { colors } from "../../theme/Colors";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";

function AttendanceCalendar() {
  const navigate = useNavigate();
  const [currentViewName, setCurrentViewName] = useState("Week");
  // const [activeTab, setActiveTab] = useState(0); // 0 = Calendar, 1 = Grid
  const [activeTab] = useState(1); // Forcé à Grid View - Calendar View désactivée
  
  // Debug: log des changements de vue
  useEffect(() => {
    // View changed
  }, [currentViewName]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [selectedSessionType, setSelectedSessionType] = useState("PROGRAM"); // GROUP LESSONS activé par défaut
  const [selectedWeeklySession, setSelectedWeeklySession] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null); // For DAY mode: selected day name
  const [terms, setTerms] = useState([]);
  const [courts, setCourts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionsByDay, setSessionsByDay] = useState([]); // Grouped sessions by day
  const [filteredSessions, setFilteredSessions] = useState([]); // Sessions filtrées par type
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false); // Loading pour les données d'attendance
  const [initialLoading, setInitialLoading] = useState(true); // Loading pour le chargement initial
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [openAttendanceModal, setOpenAttendanceModal] = useState({ open: false, data: null });
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [attendanceStatuses, setAttendanceStatuses] = useState({}); // {playerId: 'present'|'absent'|'excused'}
  const [attendanceGridData, setAttendanceGridData] = useState(null); // Données d'attendance pour la grille
  const [weeks, setWeeks] = useState([]); // Liste des semaines (W1-W15)
  const [openShirtNotesModal, setOpenShirtNotesModal] = useState({ open: false, player: null, classData: null });
  
  const { user } = useAuthContext();
  const access_token = user?.access_token;

  // Récupérer la liste des terms et sélectionner automatiquement le terme en cours
  useEffect(() => {
    Api.get(Urls.GET_TERMS_LIST, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then((response) => {
        const termsList = response?.data?.data || [];
        setTerms(termsList);
        
        // Trouver le terme en cours (date actuelle entre start_date et end_date)
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Réinitialiser l'heure pour la comparaison
        
        const currentTerm = termsList.find(term => {
          const startDate = new Date(term.start_date);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(term.end_date);
          endDate.setHours(23, 59, 59, 999); // Fin de journée
          
          return today >= startDate && today <= endDate;
        });
        
        // Sélectionner le terme en cours, sinon le premier terme disponible
        if (currentTerm) {
          setSelectedTerm(currentTerm);
        } else if (termsList.length > 0) {
          setSelectedTerm(termsList[0]);
        }
        
        setInitialLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching terms:", error);
        toast.error("Erreur lors du chargement des terms");
        setInitialLoading(false);
      });
  }, [access_token]);

  // Charger les sessions quand un term et une court sont sélectionnés (EXACTEMENT comme Schedule.js)
  useEffect(() => {
    if (selectedTerm && selectedCourt) { // Court obligatoire
      // Vider les sessions immédiatement pour un affichage fluide
      setSessions([]);
      setSelectedWeeklySession(null); // Reset la sélection weekly
      
      const date = new Date(selectedTerm.start_date);
      date.setDate(1);
      const firstDay = new Date(date + 1);
      date.setMonth(date.getMonth() + 1);
      date.setDate(1);
      const lastDay = new Date(date);
      
      if (selectedTerm?.id && selectedCourt?.id) {
        console.log('Loading sessions for:', { termId: selectedTerm.id, courtId: selectedCourt.id, sessionType: selectedSessionType });
        
        // Charger d'abord toutes les sessions, puis filtrer par type
        Api.get(Urls.GET_GLOBAL_SCHEDULE + selectedTerm.id + "/courtSchedules", {
          headers: { "Content-Type": "application/json", authorization: `Bearer ${access_token}` },
          params: {
            courtId: selectedCourt.id, // Court obligatoire
            ...(selectedDate && { startDatetime: new Date(selectedTerm.start_date) }),
            ...(selectedDate && { endDatetime: new Date(selectedTerm.end_date) }),
          },
        })
          .then((res) => {
            console.log('API response:', res?.data);
            let data = [];
            if (res?.data?.data?.length > 0) {
              for (let index = 0; index < res?.data?.data?.length; index++) {
                const element = res?.data?.data[index];
                data.push({ ...element, startDate: element?.start_datetime, endDate: element?.end_datetime });
              }
              console.log('Processed sessions:', data);
              setSessions(data);
            } else {
              console.log('No sessions found in API response');
            }
          })
          .catch((err) => {
            console.error('Error loading sessions:', err);
            toast?.error(err?.response?.data?.message);
          });
      }
    } else {
      // Si pas de court sélectionnée, vider les sessions mais garder le calendrier visible
      setSessions([]);
      setSelectedWeeklySession(null);
    }
  }, [selectedTerm, selectedCourt, access_token]); // Retiré selectedSessionType des dépendances

  // Charger les sessions hebdomadaires pour la grille d'attendance (uniquement pour Grid View)
  useEffect(() => {
    if (selectedTerm) { // Grid View toujours active
      loadWeeklySessions();
    } else {
      // Si pas de terme sélectionné, vider les sessions et réinitialiser
      setFilteredSessions([]);
      setSelectedWeeklySession(null);
      setAttendanceGridData(null);
      setWeeks([]);
      setLoading(false); // S'assurer que le loader est arrêté
    }
  }, [selectedTerm, selectedSessionType, selectedCourt]); // Recharger jours/sessions quand la court change

  const loadWeeklySessions = async () => {
    if (!selectedTerm) {
      setFilteredSessions([]);
      return;
    }
    
    try {
      const response = await Api.get(`${Urls.GET_ATTENDANCE_SESSIONS}${selectedTerm.id}/sessions`, {
        headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` }
      });
      
      // Handle new grouped structure by day: [{ day_name, day_abbreviation, sessions: [...] }, ...]
      // Or old flat structure: [session, session, ...]
      const responseData = (response.data.data || []).filter(item => item != null);
      
      // Check if data is grouped by day (new structure)
      const isGrouped = responseData.length > 0 && responseData[0]?.sessions && Array.isArray(responseData[0].sessions);
      
      let allSessions = [];
      let groupedByDay = [];
      
      if (isGrouped) {
        // Store grouped structure for day selection - filter out invalid groups
        groupedByDay = responseData.filter(dayGroup => 
          dayGroup && dayGroup.day_name && Array.isArray(dayGroup.sessions)
        );
        // Flatten grouped structure for filtering and selection
        allSessions = groupedByDay.flatMap(dayGroup => (dayGroup.sessions || []).filter(s => s != null));
      } else {
        // Old flat structure - manually group by day
        const dayGroups = {};
        responseData.forEach(session => {
          if (!session || !session.day_name) return; // Skip invalid sessions
          const dayName = session.day_name.toLowerCase() || 'unknown';
          if (!dayGroups[dayName]) {
            const dayNameUpper = session.day_name || '';
            dayGroups[dayName] = {
              day_name: dayName,
              day_abbreviation: dayNameUpper ? dayNameUpper.substring(0, 3).charAt(0).toUpperCase() + dayNameUpper.substring(1, 3).toLowerCase() : 'N/A',
              sessions: []
            };
          }
          dayGroups[dayName].sessions.push(session);
        });
        // Sort by day order
        const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        groupedByDay = Object.values(dayGroups)
          .filter(group => group.day_name && group.day_name !== 'unknown')
          .sort((a, b) => {
            const aIndex = a.day_name ? dayOrder.indexOf(a.day_name.toLowerCase()) : 999;
            const bIndex = b.day_name ? dayOrder.indexOf(b.day_name.toLowerCase()) : 999;
            return aIndex - bIndex;
          });
        allSessions = responseData;
      }
      
      // Filtrer les sessions groupées par jour selon le type de session
      let filteredGroupedByDay = groupedByDay;
      if (selectedSessionType && isGrouped) {
        filteredGroupedByDay = groupedByDay.map(dayGroup => ({
          ...dayGroup,
          sessions: (dayGroup.sessions || []).filter(session => session.session_type === selectedSessionType)
        })).filter(dayGroup => (dayGroup.sessions || []).length > 0); // Remove days with no sessions after filtering
        
        // Check if currently selected day still exists after filtering
        if (selectedDay && !filteredGroupedByDay.find(day => day.day_name === selectedDay.day_name)) {
          console.log('Selected day no longer available after filtering, clearing selection');
          setSelectedDay(null);
          setAttendanceGridData(null);
        }
      } else if (!isGrouped && selectedSessionType) {
        // Old flat structure - manually filter and re-group
        const filteredSessions = allSessions.filter(session => session.session_type === selectedSessionType);
        const dayGroups = {};
        filteredSessions.forEach(session => {
          if (!session || !session.day_name) return;
          const dayName = session.day_name.toLowerCase() || 'unknown';
          if (!dayGroups[dayName]) {
            const dayNameUpper = session.day_name || '';
            dayGroups[dayName] = {
              day_name: dayName,
              day_abbreviation: dayNameUpper ? dayNameUpper.substring(0, 3).charAt(0).toUpperCase() + dayNameUpper.substring(1, 3).toLowerCase() : 'N/A',
              sessions: []
            };
          }
          dayGroups[dayName].sessions.push(session);
        });
        const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        filteredGroupedByDay = Object.values(dayGroups)
          .filter(group => group.day_name && group.day_name !== 'unknown')
          .sort((a, b) => {
            const aIndex = a.day_name ? dayOrder.indexOf(a.day_name.toLowerCase()) : 999;
            const bIndex = b.day_name ? dayOrder.indexOf(b.day_name.toLowerCase()) : 999;
            return aIndex - bIndex;
          });
      }
      
      // Filtrer par court (aligné avec la grille / API)
      let displayGroupedByDay = filteredGroupedByDay;
      if (selectedCourt?.id) {
        displayGroupedByDay = filteredGroupedByDay
          .map(dayGroup => ({
            ...dayGroup,
            sessions: (dayGroup.sessions || []).filter(s => s && s.court_id === selectedCourt.id)
          }))
          .filter(dayGroup => (dayGroup.sessions || []).length > 0);
        if (selectedDay && !displayGroupedByDay.find(day => day.day_name === selectedDay.day_name)) {
          setSelectedDay(null);
          setAttendanceGridData(null);
        }
      }
      setSessionsByDay(displayGroupedByDay);

      // Filtrer par type de session pour les sessions individuelles (backward compatibility)
      let filtered = selectedSessionType
        ? allSessions.filter(session => session.session_type === selectedSessionType)
        : [...allSessions];
      if (selectedCourt?.id) {
        filtered = filtered.filter(s => s && s.court_id === selectedCourt.id);
      }

      console.log('Weekly sessions loaded:', {
        total: allSessions.length,
        filtered: filtered.length,
        type: selectedSessionType,
        courtId: selectedCourt?.id,
        isGrouped,
        groupedByDay: displayGroupedByDay.length,
        displayGroupedByDay: displayGroupedByDay.map(d => ({ day: d.day_name, sessions: d.sessions.length }))
      });
      setFilteredSessions(filtered);
      setSessions(allSessions);
    } catch (error) {
      console.error('Error loading weekly sessions:', error);
      toast.error('Error loading weekly sessions');
      setFilteredSessions([]);
    }
  };

  // Charger les données d'attendance quand une session weekly OU un jour est sélectionné
  useEffect(() => {
    if ((selectedWeeklySession || selectedDay) && selectedTerm) {
      loadAttendanceGridData();
    } else {
      // Si pas de session/jour ou pas de terme, vider les données et arrêter le loader
      setAttendanceGridData(null);
      setWeeks([]);
      setLoading(false); // S'assurer que le loader est arrêté
    }
  }, [selectedWeeklySession, selectedDay, selectedTerm, selectedSessionType, selectedCourt]);

  const loadAttendanceGridData = async () => {
    // Vérifier que les conditions sont remplies avant de charger
    if ((!selectedWeeklySession && !selectedDay) || !selectedTerm) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    // Build params - use day_name if selectedDay is set, otherwise use court_weekly_schedule_id
    // Declare params outside try block so it's accessible in catch block
    let params = {
      term_id: selectedTerm.id
    };
    
    try {
      if (selectedDay) {
        // Ensure selectedDay has day_name property
        const dayName = selectedDay.day_name || selectedDay;
        if (typeof dayName === 'string') {
          params.day_name = dayName.toLowerCase();
        } else {
          throw new Error('Invalid day selection');
        }
        
        // Add session type filter if selected (for DAY mode)
        if (selectedSessionType) {
          params.session_type = selectedSessionType;
        }
      } else if (selectedWeeklySession) {
        params.court_weekly_schedule_id = selectedWeeklySession;
      } else {
        throw new Error('No day or session selected');
      }

      if (selectedCourt?.id) {
        params.court_id = selectedCourt.id;
      }
      
      console.log('Loading attendance grid with params:', params);
      
      const response = await Api.get(Urls.GET_ATTENDANCE_GRID, {
        params: params,
        headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` }
      });
      
      // Handle both success: true and success: false responses
      if (response.data.success === false) {
        // Backend returned an error response (e.g., "No sessions found for this day")
        const errorMessage = response.data.message || 'Failed to load attendance data';
        setAttendanceGridData(null);
        setWeeks([]);
        toast.warning(errorMessage);
        return;
      }
      
      if (response.data.success) {
        console.log('Attendance data received:', {
          attendance_grid: response.data.data.attendance_grid,
          weeks: response.data.data.weeks,
          players_count: response.data.data.attendance_grid?.[0]?.players?.length || 0
        });
        
        if (response.data.data.attendance_grid && response.data.data.attendance_grid.length > 0) {
          const firstClass = response.data.data.attendance_grid[0];
          console.log('First class data:', {
            class_label: firstClass.class_label,
            players_count: firstClass.players?.length || 0,
            players: firstClass.players
          });
          
          if (firstClass.players && firstClass.players.length > 0) {
            const firstPlayer = firstClass.players[0];
            console.log('First player sample:', firstPlayer);
          }
        }
        setAttendanceGridData(response.data.data);
        setWeeks(response.data.data.weeks || []);
      } else {
        throw new Error(response.data.message || 'Failed to load attendance data');
      }
    } catch (error) {
      console.error('Error loading attendance data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        params: params,
        selectedDay: selectedDay,
        selectedWeeklySession: selectedWeeklySession
      });
      toast.error(error.response?.data?.message || error.message || 'Error loading attendance data');
      // En cas d'erreur, s'assurer que le loader est arrêté
      setAttendanceGridData(null);
      setWeeks([]);
    } finally {
      setLoading(false);
    }
  };

  const markAttendance = async (playerId, weekLabel, status, notes = '') => {
    if (!attendanceGridData || !attendanceGridData.attendance_grid || attendanceGridData.attendance_grid.length === 0) {
      toast.error('No attendance data available');
      return;
    }

    const classData = attendanceGridData.attendance_grid[0];
    const player = classData.players.find(p => p.player_id === playerId);
    
    if (!player) {
      toast.error('Player data not found');
      return;
    }

    // Récupérer le court_schedule_id depuis les données d'attendance
    const attendanceData = player.attendance?.[weekLabel];
    const courtScheduleId = attendanceData?.court_schedule_id;

    if (!courtScheduleId) {
      toast.error('No schedule found for this week. This week may not have a session scheduled yet.');
      return;
    }

    // Vérifier que user existe
    if (!user) {
      toast.error('User information not available. Please log in again.');
      return;
    }

    // Essayer différentes propriétés possibles pour l'ID
    const coachId = user.id || user.user_id || user.coach_id || (user.data && user.data.id) || (user.user && user.user.id);
    
    if (!coachId) {
      toast.error('Coach ID not found. Please log in again.');
      return;
    }

    // Sauvegarder l'état précédent pour pouvoir le restaurer en cas d'erreur
    const previousAttendanceData = JSON.parse(JSON.stringify(attendanceGridData));

    // Mise à jour optimiste : changer le statut visuellement immédiatement
    setAttendanceGridData(prevData => {
      if (!prevData || !prevData.attendance_grid || prevData.attendance_grid.length === 0) {
        return prevData;
      }

      const updatedData = JSON.parse(JSON.stringify(prevData));
      const classData = updatedData.attendance_grid[0];
      const player = classData.players.find(p => p.player_id === playerId);
      
      if (player && player.attendance) {
        if (!player.attendance[weekLabel]) {
          player.attendance[weekLabel] = {
            status: null,
            notes: null,
            marked_at: null,
            court_schedule_id: courtScheduleId
          };
        }
        
        player.attendance[weekLabel] = {
          ...player.attendance[weekLabel],
          status: status,
          notes: notes || player.attendance[weekLabel]?.notes || null,
          marked_at: new Date().toISOString(),
          court_schedule_id: courtScheduleId
        };
      }

      return updatedData;
    });

    // Envoyer la requête en arrière-plan
    setSavingAttendance(true);
    Api.patch(Urls.MARK_ATTENDANCE, {
      player_id: playerId,
      court_schedule_id: courtScheduleId,
      status: status,
      notes: notes || '',
      coach_id: coachId
    }, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` }
    })
    .then((response) => {
      if (response.data.success) {
        // Succès silencieux - pas besoin de toast car le changement visuel est déjà fait
        // Optionnel : toast discret
        // toast.success(`✓ ${status}`, { autoClose: 1000 });
      }
    })
    .catch((error) => {
      console.error('Error marking attendance:', error);
      
      // Restaurer l'état précédent en cas d'erreur
      setAttendanceGridData(previousAttendanceData);
      
      toast.error(`Failed to mark attendance: ${error.response?.data?.message || 'Unknown error'}`);
    })
    .finally(() => {
      setSavingAttendance(false);
    });
  };

  const getAttendanceStatus = (player, weekLabel) => {
    if (!player.attendance || !player.attendance[weekLabel]) {
      return null;
    }
    return player.attendance[weekLabel].status;
  };

  const getAttendanceNotes = (player, weekLabel) => {
    if (!player.attendance || !player.attendance[weekLabel]) {
      return null;
    }
    return player.attendance[weekLabel].notes;
  };

  const handleExport = async (format) => {
    if (!selectedTerm || (!selectedWeeklySession && !selectedDay)) {
      toast.error('Please select a term and a day or weekly session');
      return;
    }

    try {
      const url = format === 'excel' 
        ? Urls.EXPORT_ATTENDANCE_EXCEL
        : Urls.EXPORT_ATTENDANCE_PDF;

      let params = {
        term_id: selectedTerm.id
      };

      // Add day_name or court_weekly_schedule_id based on selection
      if (selectedDay) {
        const dayName = selectedDay.day_name || selectedDay;
        if (typeof dayName === 'string') {
          params.day_name = dayName.toLowerCase();
        }
        if (selectedSessionType) {
          params.session_type = selectedSessionType;
        }
      } else if (selectedWeeklySession) {
        params.court_weekly_schedule_id = selectedWeeklySession;
      }

      if (selectedCourt?.id) {
        params.court_id = selectedCourt.id;
      }

      const response = await Api.get(url, {
        params: params,
        headers: { 
          authorization: `Bearer ${access_token}` 
        },
        responseType: 'blob'
      });

      // Create blob URL and download
      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `attendance_${format}_${Date.now()}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      toast.success(`Attendance grid exported to ${format.toUpperCase()} successfully`);
    } catch (error) {
      console.error('Error exporting:', error);
      // Si c'est une erreur de blob (JSON error response), essayer de lire le message
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const jsonError = JSON.parse(text);
          toast.error(`Failed to export to ${format.toUpperCase()}: ${jsonError.message || 'Export failed'}`);
        } catch {
          toast.error(`Failed to export to ${format.toUpperCase()}: Export failed`);
        }
      } else {
        toast.error(`Failed to export to ${format.toUpperCase()}: ${error.response?.data?.message || error.message || 'Export failed'}`);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'success';
      case 'absent': return 'error';
      case 'excused': return 'warning';
      default: return 'default';
    }
  };

  // Charger la liste des courts et sélectionner automatiquement la première
  useEffect(() => {
    Api.get(Urls.GET_COURTS_LIST, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then((response) => {
        const courtsList = response?.data?.data || [];
        setCourts(courtsList);
        
        // Sélectionner automatiquement la première court
        if (courtsList.length > 0) {
          setSelectedCourt(courtsList[0]);
        }
      })
      .catch((error) => {
        toast.error("Erreur lors du chargement des courts");
      });
  }, [access_token]);

  // Extraire les types uniques (court est déjà sélectionné)
  const uniqueSessionTypes = [...new Set(sessions.map(s => s.session_type))].filter(Boolean);

  // Traiter les données pour le calendrier (EXACTEMENT comme Schedule.js)
  useEffect(() => {
    if (sessions.length > 0) {
      // Les données sont déjà dans le bon format grâce à la transformation dans l'API
      setSchedule(sessions);
    } else {
      setSchedule([]);
    }
  }, [sessions]);

  const handleTermChange = (event, value) => {
    setSelectedTerm(value);
    if (value) {
      setSelectedDate(new Date(value.start_date));
    } else {
      setSelectedDate(new Date());
      setSchedule([]);
      setSessions([]);
    }
  };

  const handleCourtChange = (event, value) => {
    setSelectedCourt(value);
  };

  const handleSessionTypeChange = (event, value) => {
    setSelectedSessionType(value);
  };

  // Composant personnalisé pour afficher les créneaux (même style que Schedule.js)
  const CustomAppointmentContent = ({ data, ...restProps }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handlePopoverOpen = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = (event) => {
      if (event) event.stopPropagation();
      setAnchorEl(null);
    };

    // Customize the appointment content based on data properties (EXACTEMENT comme Schedule.js)
    const customStyles = {
      background: data?.program_color_start && data?.program_color_end ? `linear-gradient(-30deg, #${data?.program_color_start}, #${data?.program_color_end})` : data?.session_type === "PRIVATE_LESSON" ? "#1E213E" : "#005F78",
      color: data?.program_color_start ? getContrastColor(`#${data?.program_color_start}`) : "white",
      height: "100%",
      width: currentViewName !== "Week" && currentViewName !== "Day" ? "90%" : "100%",
      boxShadow: "0",
      ":hover": { filter: "brightness(0.95)" },
      filter: "brightness(1)",
      cursor: "pointer",
    };

    return (
      <>
        <Box
          onClick={() => handleAppointmentClick(data)}
          sx={customStyles}
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="relative"
        >
          {/* Render levels badges like Schedule.js */}
          {data?.levels?.length > 0 && (
            <Box position="absolute" top="4px" right="4px" display="flex" gap="4px">
              {data?.levels && data.levels.slice(0, 2).map((level, index) => (
                <Box
                  key={index}
                  borderRadius="3px"
                  padding="1px 4px"
                  bgcolor="#D0CFCF"
                >
                  <Typography fontSize="11px" fontWeight="600" color="black">
                    {(level.name || "").length > 3
                      ? (level.name || "").substring(0, 3)
                      : (level.name || "")}
                  </Typography>
                </Box>
              ))}
              {data?.levels && data.levels.length > 2 && (
                <>
                  <Box
                    borderRadius="3px"
                    padding="1px 4px"
                    bgcolor="#D0CFCF"
                    sx={{ cursor: "pointer" }}
                    onClick={handlePopoverOpen}
                  >
                    <Typography fontSize="11px" fontWeight="600" color="black">
                      •••
                    </Typography>
                  </Box>

                  <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    onClick={(e) => e.stopPropagation()}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                                         <List sx={{ p: 1, maxHeight: "200px", overflow: "auto" }}>
                       {data.levels.map((l, index) => (
                         <ListItem key={index} sx={{ py: 0.5 }}>
                           <Typography fontSize="13px">{l.name || ""}</Typography>
                         </ListItem>
                       ))}
                     </List>
                   </Popover>
                </>
              )}
            </Box>
          )}
          
          <Typography
            textAlign="center"
            color="inherit"
            fontSize="13px"
            lineHeight="14px"
            fontWeight="500"
          >
            {data?.program_name ? data?.program_name : (data.session_type === "PRIVATE_LESSON" ? "PRIVATE LESSON" : "COURT BOOKING")}
          </Typography>
        </Box>
      </>
    );
  };

  // Gérer le clic sur un créneau
  const handleAppointmentClick = (appointment) => {
    console.log("Appointment clicked:", appointment);
    setOpenAttendanceModal({ open: true, data: appointment });
    
    // Initialiser les statuts d'attendance pour tous les joueurs
    const initialStatuses = {};
    if (appointment.players) {
      appointment.players.forEach(player => {
        initialStatuses[player.id] = 'present'; // Par défaut, tous présents
      });
    }
    setAttendanceStatuses(initialStatuses);
  };

  // Mettre à jour le statut d'attendance d'un joueur (état local)
  const updateAttendanceStatus = (playerId, status) => {
    setAttendanceStatuses(prev => ({
      ...prev,
      [playerId]: status
    }));
  };

  // Sauvegarder toutes les attendances d'un coup
  const saveAllAttendance = async () => {
    setSavingAttendance(true);
    try {
      // Vérifier que coach_id existe
      if (!openAttendanceModal.data.coach_id) {
        toast.error("Coach ID not found in session data");
        return;
      }

      // Préparer toutes les requêtes d'attendance
      const attendancePromises = Object.entries(attendanceStatuses).map(([playerId, status]) => {
        const requestData = {
          player_id: parseInt(playerId),
          court_schedule_id: openAttendanceModal.data.id,
          status: status,
          notes: '',
          coach_id: openAttendanceModal.data.coach_id
        };
        
        console.log(`Saving attendance for player ${playerId}:`, requestData);
        
        return Api.patch(Urls.MARK_ATTENDANCE, requestData, {
          headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` }
        });
      });

      // Exécuter toutes les requêtes en parallèle
      const responses = await Promise.all(attendancePromises);
      
      // Vérifier que toutes les requêtes ont réussi
      const allSuccessful = responses.every(response => response.data.success);
      
      if (allSuccessful) {
        toast.success("All attendance marked successfully!");
        setOpenAttendanceModal({ open: false, data: null });
        setAttendanceStatuses({});
      } else {
        toast.error("Some attendance could not be saved");
      }
    } catch (error) {
      console.error("Error saving attendance:", error);
      toast.error("Error saving attendance");
    } finally {
      setSavingAttendance(false);
    }
  };

  // Fonctions utilitaires pour les couleurs (même que Schedule.js)
  const hexToRgb = (hex) => {
    const bigint = parseInt(hex?.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b];
  };

  const getContrastColor = (backgroundColor) => {
    const calculateLuminance = (rgb) => {
      const [r, g, b] = rgb;
      const a = [r, g, b].map(v => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });
      return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    };

    const rgb = hexToRgb(backgroundColor);
    const luminance = calculateLuminance(rgb);
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  // Fonction pour formater l'heure
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Composant pour changer la vue (même format que Schedule.js)
  const ExternalViewSwitcher = ({ currentViewName }) => {
    return (
      <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="flex-end">
        <Box display="flex" flexDirection="row">
          <Button onClick={() => setCurrentViewName("Month")} variant={currentViewName === "Month" ? "views_active_left_radius" : "views_inactive_left_radius"}>
            Month
          </Button>
          <Button onClick={() => setCurrentViewName("Week")} variant={currentViewName === "Week" ? "views_active" : "views_inactive"}>
            Week
          </Button>
          <Button onClick={() => setCurrentViewName("Day")} variant={currentViewName === "Day" ? "views_active_right_radius" : "views_inactive_right_radius"}>
            Day
          </Button>
        </Box>
      </Box>
    );
  };

  // Fonction pour rendre la vue grille
  const renderGridView = () => {
    return (
      <Box p={3}>
        
        
        {/* Tous les contrôles intégrés en haut de la grille - TOUJOURS VISIBLES */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography fontSize="16px" fontWeight="500" color="#4B465C" marginBottom="16px">
            Grid Controls
          </Typography>
          
          {/* Première ligne : Term, Court et Weekly Sessions */}
          <Box display="flex" gap="20px" alignItems="center" flexWrap="wrap" marginBottom="20px">
            {/* Sélection du Term */}
            <Box display="flex" alignItems="center" gap="12px">
              <Typography fontSize="13px" fontWeight="400" color="#4B465C">
                Term *
              </Typography>
              <Autocomplete
                sx={{ minWidth: 200 }}
                options={terms}
                value={selectedTerm}
                getOptionLabel={(term) => term.name}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={handleTermChange}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Select a term"
                    disabled={terms.length === 0}
                    size="small"
                  />
                )}
                noOptionsText="No terms available"
              />
            </Box>

            {/* Sélection de la Court */}
            <Box display="flex" alignItems="center" gap="12px">
              <Typography fontSize="13px" fontWeight="400" color="#4B465C">
                Court
              </Typography>
              <Autocomplete
                sx={{ minWidth: 200 }}
                options={courts}
                value={selectedCourt}
                getOptionLabel={(court) => court.name || "Unknown Court"}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={handleCourtChange}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Select a court (required)"
                    disabled={!selectedTerm}
                    size="small"
                  />
                )}
                noOptionsText="No courts available"
                disabled={!selectedTerm}
              />
            </Box>

            {/* Sélection du Jour (DAY MODE) */}
            <Box display="flex" alignItems="center" gap="12px">
              <Typography fontSize="13px" fontWeight="400" color="#4B465C">
                Day *
              </Typography>
              <Autocomplete
                sx={{ minWidth: 250 }}
                options={sessionsByDay.filter(day => day != null && day.day_name)}
                value={selectedDay}
                getOptionLabel={(dayGroup) => {
                  if (!dayGroup) return '';
                  const dayName = dayGroup.day_name || '';
                  const dayAbbr = dayGroup.day_abbreviation || (dayName ? dayName.substring(0, 3).charAt(0).toUpperCase() + dayName.substring(1, 3).toLowerCase() : '');
                  const year = selectedTerm && selectedTerm.start_date ? new Date(selectedTerm.start_date).getFullYear() : '';
                  // Just show day and year, without program name
                  return `${dayAbbr} ${year}`;
                }}
                isOptionEqualToValue={(option, value) => option?.day_name === value?.day_name}
                onChange={(event, newValue) => {
                  setSelectedDay(newValue);
                  setSelectedWeeklySession(null); // Clear single session selection when day is selected
                }}
                renderOption={(props, dayGroup) => {
                  if (!dayGroup) return null;
                  const dayName = dayGroup.day_name || '';
                  const dayAbbr = dayGroup.day_abbreviation || (dayName ? dayName.substring(0, 3).charAt(0).toUpperCase() + dayName.substring(1, 3).toLowerCase() : '');
                  const year = selectedTerm && selectedTerm.start_date ? new Date(selectedTerm.start_date).getFullYear() : '';
                  return (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {dayAbbr} {year}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {dayGroup.sessions?.length || 0} session(s)
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Select a day"
                    disabled={!selectedTerm || sessionsByDay.length === 0}
                    size="small"
                  />
                )}
                noOptionsText="No days available"
                disabled={!selectedTerm || sessionsByDay.length === 0}
              />
            </Box>
          </Box>
          
          {/* Deuxième ligne : Filtres sur la même ligne que les labels */}
          <Box display="flex" gap="20px" alignItems="center" flexWrap="wrap">
            {/* Filtre GROUP LESSONS (PROGRAM) */}
            <Box display="flex" gap="10px" alignItems="center">
              <Typography fontSize="16px" fontWeight="600" color="#666D83">
                GROUP LESSONS
              </Typography>
              <MySwitch
                isOn={selectedSessionType === "PROGRAM"}
                onChange={() => setSelectedSessionType("PROGRAM")}
              />
            </Box>

            {/* Filtre PRIVATE LESSONS */}
            <Box display="flex" gap="10px" alignItems="center">
              <Typography fontSize="16px" fontWeight="600" color="#666D83">
                PRIVATE LESSONS
              </Typography>
              <MySwitch
                isOn={selectedSessionType === "PRIVATE_LESSON"}
                onChange={() => setSelectedSessionType("PRIVATE_LESSON")}
              />
            </Box>

            {/* Filtre COURT BOOKINGS */}
            <Box display="flex" gap="10px" alignItems="center">
              <Typography fontSize="16px" fontWeight="600" color="#666D83">
                COURT BOOKINGS
              </Typography>
              <MySwitch
                isOn={selectedSessionType === "COURT_BOOKING"}
                onChange={() => setSelectedSessionType("COURT_BOOKING")}
              />
            </Box>
          </Box>
        </Paper>
        
        {/* Vraie grille d'attendance avec joueurs et semaines */}
        {!selectedTerm ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              Please select a term to view attendance
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Choose a term from the dropdown above
            </Typography>
          </Paper>
        ) : loading ? (
          <Paper sx={{ p: 3, minHeight: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Paper>
        ) : (!selectedWeeklySession && !selectedDay) ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              Select a day to view attendance
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Choose a day from the dropdown above to see and manage attendance
            </Typography>
          </Paper>
        ) : !attendanceGridData || !attendanceGridData.attendance_grid || attendanceGridData.attendance_grid.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              No attendance data available for this session
            </Typography>
          </Paper>
        ) : (
          attendanceGridData.attendance_grid.map((classData, classIndex) => (
            <Paper key={classIndex} sx={{ overflow: 'auto', mb: 3 }}>
              <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {classData.class_label}
                  </Typography>
                  <Typography variant="body2">
                    {classData.class_info.court_name && classData.class_info.time_range 
                      ? `${classData.class_info.court_name} - ${classData.class_info.time_range}`
                      : classData.class_info.sessions_count 
                        ? `${classData.class_info.sessions_count} session(s)`
                        : ''}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<TableChartIcon />}
                    onClick={() => handleExport('excel')}
                    sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f5f5f5' } }}
                  >
                    Excel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() => handleExport('pdf')}
                    sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f5f5f5' } }}
                  >
                    PDF
                  </Button>
                </Box>
              </Box>
              {/* Légende pour les statuts d'attendance */}
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                <Box display="flex" alignItems="center" gap={3} flexWrap="nowrap">
                  <Typography variant="body2" fontWeight="600" sx={{ whiteSpace: 'nowrap' }}>
                    Status Legend:
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        minWidth: 32,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#4caf50',
                        color: 'white',
                        borderRadius: 1,
                        fontWeight: 'bold'
                      }}
                    >
                      ✓
                    </Box>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Present</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        minWidth: 32,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#f44336',
                        color: 'white',
                        borderRadius: 1,
                        fontWeight: 'bold'
                      }}
                    >
                      X
                    </Box>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Absent</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        minWidth: 32,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#ff9800',
                        color: 'white',
                        borderRadius: 1,
                        fontWeight: 'bold'
                      }}
                    >
                      E
                    </Box>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Excused</Typography>
                  </Box>
                </Box>
              </Box>
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Age</TableCell>
                      {weeks.map((week) => {
                        // Formater la date pour l'affichage (ex: "15 Nov")
                        const weekDate = week.schedule_date ? new Date(week.schedule_date) : (week.start_date ? new Date(week.start_date) : null);
                        const formattedDate = weekDate ? weekDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '';
                        
                        return (
                          <TableCell key={week.week_number} align="center" sx={{ fontWeight: 'bold' }}>
                            <Box display="flex" flexDirection="column" alignItems="center">
                              <Typography variant="body2" fontWeight="bold">
                                {week.label}
                              </Typography>
                              {formattedDate && (
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', mt: 0.5 }}>
                                  {formattedDate}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ fontWeight: 'bold' }}>Shirts Receiv...</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Group players by level + program + coach
                      // Use backend group_label if available, otherwise group manually
                      let playersByGroup = {};
                      
                      if (classData.group_label && classData.players) {
                        // Backend already grouped - collect levels from players
                        const firstPlayer = classData.players[0];
                        const levelNamesSet = new Set();
                        classData.players.forEach(player => {
                          if (player.level_name) {
                            levelNamesSet.add(player.level_name);
                          }
                        });
                        playersByGroup[classData.group_label] = {
                          programName: classData.class_info?.program_name || firstPlayer?.program_name || '',
                          coachFullName: classData.class_info?.coach_full_name || firstPlayer?.coach_full_name || '',
                          sessionTime: firstPlayer?.session_time || null,
                          levelNames: Array.from(levelNamesSet).sort(),
                          players: classData.players
                        };
                      } else {
                        // Group by session: program + coach + session_time (not by level)
                        playersByGroup = classData.players.reduce((acc, player) => {
                          const programName = player.program_name || '';
                          const coachFullName = player.coach_full_name || (player.coach_first_name && player.coach_last_name 
                            ? `${player.coach_first_name} ${player.coach_last_name}` 
                            : '');
                          const sessionTime = player.session_time || null;
                          
                          // Create a unique group key: "Program_Coach_SessionTime"
                          const groupKey = `${programName || 'Unknown'}_${coachFullName || 'Unknown'}_${sessionTime || 'Unknown'}`;
                          
                          if (!acc[groupKey]) {
                            acc[groupKey] = {
                              programName: programName,
                              coachFullName: coachFullName,
                              sessionTime: sessionTime,
                              levelNames: new Set(), // Use Set to collect unique levels
                              players: []
                            };
                          }
                          
                          // Add level to the set for this session
                          const levelName = player.level_name || 'Unknown';
                          acc[groupKey].levelNames.add(levelName);
                          acc[groupKey].players.push(player);
                          return acc;
                        }, {});
                        
                        // Convert Sets to sorted arrays
                        Object.keys(playersByGroup).forEach(key => {
                          playersByGroup[key].levelNames = Array.from(playersByGroup[key].levelNames).sort();
                        });
                      }

                      // Create array with group headers and players
                      const rows = [];
                      Object.keys(playersByGroup).sort().forEach((groupKey) => {
                        const group = playersByGroup[groupKey];
                        // Add group header row
                        rows.push({ 
                          type: 'group-header', 
                          groupKey, 
                          levelNames: group.levelNames || [],
                          programName: group.programName,
                          coachFullName: group.coachFullName,
                          sessionTime: group.sessionTime,
                          colspan: weeks.length + 4 
                        });
                        // Add player rows for this group
                        group.players.forEach((player) => {
                          rows.push({ type: 'player', player });
                        });
                      });

                      return rows.map((row, rowIndex) => {
                        if (row.type === 'group-header') {
                          // Format header: "Class [Program] Mon [Time] [Level1], [Level2]... Coach: [Coach Name]"
                          const dayAbbr = classData.class_info?.day_abbreviation || '';
                          const programName = row.programName || '';
                          const sessionTimeStr = row.sessionTime || '';
                          const levelsStr = row.levelNames && row.levelNames.length > 0 ? row.levelNames.join(', ') : '';
                          const coachName = row.coachFullName || '';
                          
                          // Build header: "Class Red Mon 08:00 Advenced-2, Advenced-4. Coach: FAYCAL TITRI"
                          let headerParts = [];
                          if (programName && dayAbbr && sessionTimeStr) {
                            headerParts.push(`Class ${programName} ${dayAbbr} ${sessionTimeStr}`);
                          }
                          if (levelsStr) {
                            headerParts.push(levelsStr);
                          }
                          if (coachName) {
                            headerParts.push(`Coach: ${coachName}`);
                          }
                          const headerText = headerParts.join(' ');
                          
                          return (
                            <TableRow key={`group-${row.groupKey}-${rowIndex}`}>
                              <TableCell 
                                colSpan={row.colspan}
                                sx={{ 
                                  fontWeight: 'bold', 
                                  bgcolor: 'action.hover',
                                  py: 1.5,
                                  fontSize: '0.95rem'
                                }}
                              >
                                <Typography variant="body1" fontWeight="600">
                                  {headerText}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        const player = row.player;
                        return (
                      <TableRow key={player.player_id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            {player.code}
                          </Typography>
                        </TableCell>
                        <TableCell>{player.age || 'N/A'}</TableCell>
                        {weeks.map((week) => {
                          const attendanceData = player.attendance?.[week.label];
                          
                          // Player has NO booking for this week - don't show buttons
                          if (attendanceData === null || attendanceData === undefined) {
                            return (
                              <TableCell key={week.week_number} align="center">
                                {/* Empty cell - no buttons displayed */}
                              </TableCell>
                            );
                          }
                          
                          const status = getAttendanceStatus(player, week.label);
                          const notes = getAttendanceNotes(player, week.label);
                          const hasSchedule = attendanceData !== null && attendanceData?.court_schedule_id !== undefined;
                          
                          return (
                            <TableCell key={week.week_number} align="center">
                              <Box display="flex" flexDirection="column" gap={0.5} alignItems="center">
                                <Box display="flex" gap={0.5}>
                                  <Button
                                    size="small"
                                    variant={status === 'present' ? 'contained' : 'outlined'}
                                    color="success"
                                    onClick={() => markAttendance(player.player_id, week.label, 'present')}
                                    disabled={savingAttendance || !hasSchedule}
                                    sx={{ minWidth: 32, height: 24, p: 0.5 }}
                                    title={!hasSchedule ? 'No schedule for this week' : 'Mark as present'}
                                  >
                                    ✓
                                  </Button>
                                  <Button
                                    size="small"
                                    variant={status === 'absent' ? 'contained' : 'outlined'}
                                    color="error"
                                    onClick={() => markAttendance(player.player_id, week.label, 'absent')}
                                    disabled={savingAttendance || !hasSchedule}
                                    sx={{ minWidth: 32, height: 24, p: 0.5 }}
                                    title={!hasSchedule ? 'No schedule for this week' : 'Mark as absent'}
                                  >
                                    X
                                  </Button>
                                  <Button
                                    size="small"
                                    variant={status === 'excused' ? 'contained' : 'outlined'}
                                    color="warning"
                                    onClick={() => markAttendance(player.player_id, week.label, 'excused')}
                                    disabled={savingAttendance || !hasSchedule}
                                    sx={{ minWidth: 32, height: 24, p: 0.5 }}
                                    title={!hasSchedule ? 'No schedule for this week' : 'Mark as excused'}
                                  >
                                    E
                                  </Button>
                                </Box>
                                {notes && (
                                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                    {notes}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                        <TableCell 
                          onClick={() => setOpenShirtNotesModal({ open: true, player: player, classData: classData })}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          {player.shirt?.received ? (
                            <Typography variant="caption" color="success.main">
                              ✓ {player.shirt.size} {player.shirt.date ? `on ${player.shirt.date}` : ''}
                            </Typography>
                          ) : player.shirt?.size ? (
                            <Typography variant="caption" color="textSecondary">
                              X {player.shirt.size} {player.shirt.notes || ''}
                            </Typography>
                          ) : (
                            <Box display="flex" alignItems="center" justifyContent="center">
                              <AddIcon sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.6 }} />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell 
                          onClick={() => setOpenShirtNotesModal({ open: true, player: player, classData: classData })}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          {player.notes ? (
                            <Typography variant="caption" color="textSecondary">
                              {player.notes}
                            </Typography>
                          ) : (
                            <Box display="flex" alignItems="center" justifyContent="center">
                              <AddIcon sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.6 }} />
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))
        )}
      </Box>
    );
  };

  // Loader uniquement pour le chargement initial (termes, courts)
  if (initialLoading) {
    return (
      <ContentBox>
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      </ContentBox>
    );
  }

  return (
    <ContentBox>
      <Box>
        <Typography fontSize="24px" fontWeight="600" color="#4B465C" marginBottom="8px">
          Attendance Tracking
        </Typography>
        <Typography fontSize="14px" color="#666D83" marginBottom="24px">
          Select term and court to view and manage attendance
        </Typography>
        
        {/* Onglets pour basculer entre Calendar et Grid - DÉSACTIVÉ */}
        {/* <Box marginBottom="24px">
          <Tabs 
            value={activeTab} 
            onChange={(event, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: '500'
              }
            }}
          >
            <Tab label="Calendar View" />
            <Tab label="Grid View" />
          </Tabs>
        </Box> */}
        
        {/* Bouton pour accéder à la grille (maintenant remplacé par les onglets) */}
        {/* <Box display="flex" gap="16px" marginBottom="24px">
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/admin/attendance/grid')}
            sx={{ textTransform: 'none' }}
          >
            View Attendance Grid
          </Button>
        </Box> */}

        {/* Layout avec sidebar et calendrier (comme Schedule.js) - DÉSACTIVÉ */}
        {/* <Box display="flex" gap="0"> */}
        {/* Sidebar des filtres - SEULEMENT dans la vue calendrier - DÉSACTIVÉ */}
        {/* {activeTab === 0 && ( */}
        {/*   <Box flex={1} padding="20px" borderRight="1px solid lightgray"> */}
        {/* Tout le contenu de la sidebar est commenté ci-dessous */}
        {/* 
              Sélection du Term
              <Box marginBottom="24px">
                <Typography fontSize="13px" fontWeight="400" color="#4B465C" marginBottom="8px">
                  Term *
                </Typography>
                <Autocomplete
                  sx={{ minWidth: "100%" }}
                  options={terms}
                  value={selectedTerm}
                  getOptionLabel={(term) => term.name}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={handleTermChange}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      placeholder="Select a term"
                      disabled={terms.length === 0}
                    />
                  )}
                  noOptionsText="No terms available"
                />
              </Box>

              Sélection de la Court
              <Box marginBottom="24px">
                <Typography fontSize="13px" fontWeight="400" color="#4B465C" marginBottom="8px">
                  Court
                </Typography>
                <Autocomplete
                  sx={{ minWidth: "100%" }}
                  options={courts}
                  value={selectedCourt}
                  getOptionLabel={(court) => court.name || "Unknown Court"}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={handleCourtChange}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      placeholder="Select a court (required)"
                      disabled={!selectedTerm}
                    />
                  )}
                  noOptionsText="No courts available"
                  disabled={!selectedTerm}
                />
              </Box>

              Filtres par type de session (toggle switches) - SEULEMENT dans la vue calendrier - DÉSACTIVÉ
              {activeTab === 0 && (
                <Box>
                  <Box marginBottom="16px">
                    <Typography fontSize="13px" fontWeight="400" lineHeight="20px" color="#4B465C">
                      Filter
                    </Typography>
                  </Box>
                  
                  Filtre GROUP LESSONS (PROGRAM) - EN PREMIER
                  <Box display="flex" gap="10px" alignItems="center" marginBottom="16px">
                    <Typography fontSize="20px" fontWeight="600" color="#666D83">
                      GROUP LESSONS
                    </Typography>
                    <MySwitch
                      isOn={selectedSessionType === "PROGRAM"}
                      onChange={() => {
                        const newValue = selectedSessionType === "PROGRAM" ? null : "PROGRAM";
                        setSelectedSessionType(newValue);
                      }}
                    />
                  </Box>

                  Filtre PRIVATE LESSONS
                  <Box display="flex" gap="10px" alignItems="center" marginBottom="16px">
                    <Typography fontSize="20px" fontWeight="600" color="#666D83">
                      PRIVATE LESSONS
                    </Typography>
                    <MySwitch
                      isOn={selectedSessionType === "PRIVATE_LESSON"}
                      onChange={() => {
                        const newValue = selectedSessionType === "PRIVATE_LESSON" ? null : "PRIVATE_LESSON";
                        setSelectedSessionType(newValue);
                      }}
                    />
                  </Box>

                  Filtre COURT BOOKINGS
                  <Box display="flex" gap="10px" alignItems="center" marginBottom="16px">
                    <Typography fontSize="20px" fontWeight="600" color="#666D83">
                      COURT BOOKINGS
                    </Typography>
                    <MySwitch
                      isOn={selectedSessionType === "COURT_BOOKING"}
                      onChange={() => {
                        const newValue = selectedSessionType === "COURT_BOOKING" ? null : "COURT_BOOKING";
                        setSelectedSessionType(newValue);
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        */}
        {/* Fin du commentaire de la sidebar */}

          {/* Contenu principal selon l'onglet actif - DÉSACTIVÉ */}
          {/* <Box flex={activeTab === 0 ? 4 : 1} borderLeft={activeTab === 0 ? "1px solid lightgray" : "none"}> */}
          {/* {activeTab === 0 ? ( */}
          {/* Calendrier - EXACTEMENT comme Schedule.js - DÉSACTIVÉ */}
          {/* <Scheduler data={schedule} height={700}>
                <ViewState 
                  currentDate={selectedDate} 
                  currentViewName={currentViewName}
                  onCurrentViewNameChange={setCurrentViewName}
                  onCurrentDateChange={setSelectedDate}
                />
                <WeekView cellDuration={30} startDayHour={6} endDayHour={22} />
                <MonthView />
                <DayView cellDuration={30} startDayHour={6} endDayHour={22} />
                <Appointments appointmentComponent={CustomAppointmentContent} />
                
                <Toolbar />
                <Box marginLeft="20px" display="flex" alignItems="center" justifyContent="space-between">
                  <Typography fontSize="18px" fontWeight="500" lineHeight="24px" color="#4B465C">
                    TERM : {selectedTerm?.name || "Select a term"} {selectedCourt ? ` / ${selectedCourt.name}` : ""}
                  </Typography>
                </Box>
                <DateNavigator onCurrentDateChange={setSelectedDate} />
                <ExternalViewSwitcher currentViewName={currentViewName} />
              </Scheduler>
            ) : (
              Vue grille - AFFICHÉE DIRECTEMENT
              renderGridView()
            )} */}
          {/* </Box> */}
        {/* </Box> */}
        
        {/* Afficher directement la Grid View (Calendar View désactivée) */}
        {renderGridView()}
      </Box>

      {/* Modal d'attendance */}
      <Modal
        open={openAttendanceModal.open}
        onClose={() => setOpenAttendanceModal({ open: false, data: null })}
        aria-labelledby="attendance-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography id="attendance-modal-title" variant="h6" component="h2" marginBottom="16px">
            Mark Attendance - {openAttendanceModal.data?.title}
          </Typography>
          
          {openAttendanceModal.data && (
            <Box>
              <Typography variant="body2" color="text.secondary" marginBottom="16px">
                Court: {openAttendanceModal.data.court || openAttendanceModal.data.court_name} | 
                Date: {openAttendanceModal.data.startDate ? new Date(openAttendanceModal.data.startDate).toLocaleDateString() : 'N/A'} | 
                Time: {openAttendanceModal.data.startDate ? new Date(openAttendanceModal.data.startDate).toLocaleTimeString() : 'N/A'} - {openAttendanceModal.data.endDate ? new Date(openAttendanceModal.data.endDate).toLocaleTimeString() : 'N/A'}
              </Typography>
              

              
              {/* Liste des joueurs avec gestion de l'attendance */}
              <Box marginBottom="16px">
                <Typography variant="h6" marginBottom="12px">
                  Players ({openAttendanceModal.data?.players?.length || 0})
                </Typography>
                
                {openAttendanceModal.data?.players?.map((player) => (
                  <Box 
                    key={player.id} 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="space-between"
                    padding="12px"
                    marginBottom="8px"
                    border="1px solid #e0e0e0"
                    borderRadius="8px"
                    bgcolor="#fafafa"
                  >
                    {/* Info du joueur */}
                    <Box display="flex" alignItems="center" gap="12px">
                      <Avatar 
                        src={player.url_avatar} 
                        sx={{ width: 40, height: 40 }}
                      >
                        {player.first_name?.[0]}{player.last_name?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="500">
                          {player.first_name} {player.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {player.name}
                        </Typography>
                      </Box>
                    </Box>
                    
                                         {/* Checkboxes d'attendance */}
                     <Box display="flex" gap="8px" alignItems="center">
                       <Box display="flex" alignItems="center" gap="4px">
                         <input
                           type="radio"
                           name={`attendance_${player.id}`}
                           id={`present_${player.id}`}
                           value="present"
                           checked={attendanceStatuses[player.id] === 'present'}
                           onChange={() => updateAttendanceStatus(player.id, 'present')}
                           style={{ accentColor: '#4caf50' }}
                         />
                         <label htmlFor={`present_${player.id}`} style={{ color: '#4caf50', fontSize: '12px', fontWeight: '500' }}>
                           Present
                         </label>
                       </Box>
                       
                       <Box display="flex" alignItems="center" gap="4px">
                         <input
                           type="radio"
                           name={`attendance_${player.id}`}
                           id={`absent_${player.id}`}
                           value="absent"
                           checked={attendanceStatuses[player.id] === 'absent'}
                           onChange={() => updateAttendanceStatus(player.id, 'absent')}
                           style={{ accentColor: '#f44336' }}
                         />
                         <label htmlFor={`absent_${player.id}`} style={{ color: '#f44336', fontSize: '12px', fontWeight: '500' }}>
                           X
                         </label>
                       </Box>
                       
                       <Box display="flex" alignItems="center" gap="4px">
                         <input
                           type="radio"
                           name={`attendance_${player.id}`}
                           id={`excused_${player.id}`}
                           value="excused"
                           checked={attendanceStatuses[player.id] === 'excused'}
                           onChange={() => updateAttendanceStatus(player.id, 'excused')}
                           style={{ accentColor: '#ff9800' }}
                         />
                         <label htmlFor={`excused_${player.id}`} style={{ color: '#ff9800', fontSize: '12px', fontWeight: '500' }}>
                           E
                         </label>
                       </Box>
                     </Box>
                  </Box>
                ))}
                
                {(!openAttendanceModal.data?.players || openAttendanceModal.data.players.length === 0) && (
                  <Typography variant="body2" color="text.secondary" textAlign="center" padding="20px">
                    No players assigned to this session
                  </Typography>
                )}
              </Box>
              
              <Box display="flex" gap="8px" justifyContent="flex-end">
                <Button 
                  variant="outlined" 
                  onClick={() => setOpenAttendanceModal({ open: false, data: null })}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  color="primary"
                  disabled={savingAttendance}
                  onClick={saveAllAttendance}
                >
                  {savingAttendance ? <CircularProgress size={20} /> : "Save All Attendance"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Modal pour Shirts et Notes */}
      <Modal 
        open={openShirtNotesModal.open} 
        onClose={() => setOpenShirtNotesModal({ open: false, player: null, classData: null })}
      >
        <Box sx={modalStyleVertical}>
          <Box position="absolute" top="2px" right="2px">
            <ButtonSquare
              action="close"
              onClick={() => setOpenShirtNotesModal({ open: false, player: null, classData: null })}
            />
          </Box>
          <Box>
            <Typography fontSize="22px" fontWeight="500" lineHeight="32px" color="#4B465C" marginBottom="24px">
              Shirts & Notes - {openShirtNotesModal.player?.code || 'Player'}
            </Typography>
            
            <Formik
              initialValues={{
                shirt_size: openShirtNotesModal.player?.shirt?.size || '',
                shirt_received: openShirtNotesModal.player?.shirt?.received || false,
                shirt_date: openShirtNotesModal.player?.shirt?.date || '',
                shirt_notes: openShirtNotesModal.player?.shirt?.notes || '',
                player_notes: openShirtNotesModal.player?.notes || ''
              }}
              onSubmit={async (values) => {
                try {
                  await Api.patch(
                    Urls.UPDATE_SHIRT_NOTES,
                    {
                      player_id: openShirtNotesModal.player?.player_id,
                      court_weekly_schedule_id: openShirtNotesModal.classData?.class_info?.id || selectedWeeklySession,
                      shirt_size: values.shirt_size || null,
                      shirt_received: values.shirt_received || false,
                      shirt_date: values.shirt_date || null,
                      shirt_notes: values.shirt_notes || null,
                      notes: values.player_notes || null
                    },
                    {
                      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` }
                    }
                  );
                  toast.success('Shirts and notes saved successfully');
                  setOpenShirtNotesModal({ open: false, player: null, classData: null });
                  // Recharger les données d'attendance
                  if (selectedTerm && selectedWeeklySession) {
                    loadAttendanceGridData();
                  }
                } catch (error) {
                  console.error('Error saving shirt and notes:', error);
                  toast.error(error?.response?.data?.message || 'Error saving shirt and notes');
                }
              }}
            >
              {({ values, setFieldValue, handleSubmit }) => (
                <Form>
                  <Box display="flex" flexDirection="column" gap="20px">
                    {/* Section Shirts */}
                    <Box>
                      <Typography fontSize="18px" fontWeight="500" color="#4B465C" marginBottom="16px">
                        Shirts Received
                      </Typography>
                      <Box display="flex" flexDirection="column" gap="16px">
                        <Box>
                          <Typography fontSize="13px" fontWeight="400" color="#4B465C" marginBottom="8px">
                            Shirt Size
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            value={values.shirt_size}
                            onChange={(e) => setFieldValue('shirt_size', e.target.value)}
                            placeholder="e.g., S, M, L, XL"
                          />
                        </Box>
                        <Box display="flex" alignItems="center" gap="12px">
                          <input
                            type="checkbox"
                            checked={values.shirt_received}
                            onChange={(e) => setFieldValue('shirt_received', e.target.checked)}
                            style={{ width: 18, height: 18 }}
                          />
                          <Typography fontSize="13px" fontWeight="400" color="#4B465C">
                            Shirt Received
                          </Typography>
                        </Box>
                        {values.shirt_received && (
                          <Box>
                            <Typography fontSize="13px" fontWeight="400" color="#4B465C" marginBottom="8px">
                              Date Received
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              type="date"
                              value={values.shirt_date}
                              onChange={(e) => setFieldValue('shirt_date', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Box>
                        )}
                        <Box>
                          <Typography fontSize="13px" fontWeight="400" color="#4B465C" marginBottom="8px">
                            Shirt Notes
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            value={values.shirt_notes}
                            onChange={(e) => setFieldValue('shirt_notes', e.target.value)}
                            placeholder="Additional notes about the shirt"
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Section Notes */}
                    <Box>
                      <Typography fontSize="18px" fontWeight="500" color="#4B465C" marginBottom="16px">
                        Notes
                      </Typography>
                      <Box>
                        <Typography fontSize="13px" fontWeight="400" color="#4B465C" marginBottom="8px">
                          Player Notes
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={4}
                          value={values.player_notes}
                          onChange={(e) => setFieldValue('player_notes', e.target.value)}
                          placeholder="Add notes about this player"
                        />
                      </Box>
                    </Box>

                    {/* Boutons */}
                    <Box display="flex" gap="16px" justifyContent="flex-end" marginTop="16px">
                      <Button
                        variant="outlined"
                        onClick={() => setOpenShirtNotesModal({ open: false, player: null, classData: null })}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Modal>

      <ToastContainer />
    </ContentBox>
  );
}

export default AttendanceCalendar;
